import React, { useState, useRef, useEffect } from 'react';

export interface DateRange {
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const fmt = (d: Date) => d.toISOString().substring(0, 10);
const today = () => fmt(new Date());

const startOfWeek = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return fmt(d);
};

const endOfWeek = () => {
  const d = new Date();
  d.setDate(d.getDate() + (6 - d.getDay()));
  return fmt(d);
};

const startOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const endOfMonth = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return fmt(last);
};

const SHORTCUTS = [
  { label: 'Hoje', fn: () => ({ start: today(), end: today() }) },
  { label: 'Esta semana', fn: () => ({ start: startOfWeek(), end: endOfWeek() }) },
  { label: 'Este mês', fn: () => ({ start: startOfMonth(), end: endOfMonth() }) },
];

const formatLabel = (range: DateRange) => {
  const [sy, sm, sd] = range.start.split('-');
  const [ey, em, ed] = range.end.split('-');
  const startPretty = `${sd}/${sm}/${sy}`;
  const endPretty = `${ed}/${em}/${ey}`;
  if (range.start === range.end) return startPretty;
  return `${sd}/${sm} → ${ed}/${em}/${ey}`;
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  const now = new Date();
  const [viewDate, setViewDate] = useState(
    new Date(parseInt(value.start.split('-')[0]), parseInt(value.start.split('-')[1]) - 1, 1)
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSelecting('start');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y: number, m: number) => new Date(y, m, 1).getDay();

  const changeMonth = (offset: number) => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1));
  };

  const handleDayClick = (dateStr: string) => {
    if (selecting === 'start') {
      onChange({ start: dateStr, end: dateStr });
      setSelecting('end');
    } else {
      if (dateStr < value.start) {
        onChange({ start: dateStr, end: value.start });
      } else {
        onChange({ start: value.start, end: dateStr });
      }
      setSelecting('start');
      setIsOpen(false);
    }
  };

  const renderDays = () => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    const total = daysInMonth(y, m);
    const firstDay = firstDayOfMonth(y, m);
    const days: React.ReactNode[] = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`b${i}`} />);
    }

    for (let d = 1; d <= total; d++) {
      const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isStart = dateStr === value.start;
      const isEnd = dateStr === value.end;
      const isToday = dateStr === today();

      // Calculate in-range considering hover for live preview
      const rangeEnd = selecting === 'end' && hoverDate ? hoverDate : value.end;
      const lo = value.start < rangeEnd ? value.start : rangeEnd;
      const hi = value.start < rangeEnd ? rangeEnd : value.start;
      const inRange = dateStr > lo && dateStr < hi;
      const isRangeEdge = dateStr === lo || dateStr === hi;

      days.push(
        <button
          key={d}
          onClick={() => handleDayClick(dateStr)}
          onMouseEnter={() => selecting === 'end' && setHoverDate(dateStr)}
          onMouseLeave={() => setHoverDate(null)}
          className={`
            relative h-8 w-full rounded-lg text-[11px] font-bold transition-all flex items-center justify-center
            ${isRangeEdge ? 'bg-primary text-white shadow-lg shadow-primary/30 scale-105 z-10' : ''}
            ${inRange ? 'bg-primary/15 text-primary rounded-none' : ''}
            ${!isRangeEdge && !inRange ? 'text-slate-400 hover:bg-slate-700/40 hover:text-white' : ''}
            ${isToday && !isRangeEdge ? 'text-primary font-black' : ''}
          `}
        >
          {d}
          {isToday && !isRangeEdge && (
            <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
          )}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => { setIsOpen(!isOpen); setSelecting('start'); }}
        className="flex items-center gap-2 bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm cursor-pointer hover:border-slate-600 transition-all text-slate-300 min-w-[200px]"
      >
        <span className="material-symbols-outlined text-primary text-[18px]">date_range</span>
        <span className="font-semibold flex-1">{formatLabel(value)}</span>
        <span className="material-symbols-outlined text-slate-600 text-[16px]">expand_more</span>
      </div>

      {isOpen && (
        <div
          className="absolute top-full mt-2 left-0 z-[60] w-80 border border-slate-700/60 rounded-2xl shadow-2xl p-4 animate-fade-in-up bg-slate-900"
        >
          {/* Atalhos */}
          <div className="flex gap-2 mb-4">
            {SHORTCUTS.map(s => (
              <button
                key={s.label}
                onClick={() => { onChange(s.fn()); setIsOpen(false); setSelecting('start'); }}
                className="flex-1 text-[10px] font-bold py-1.5 px-2 rounded-lg bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-all"
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Hint */}
          <p className="text-[10px] text-slate-600 text-center mb-3">
            {selecting === 'start' ? '↖ Clique no dia de início' : '↖ Clique no dia de fim (ou o mesmo dia)'}
          </p>

          {/* Header navegação */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-slate-700/50 rounded-lg text-slate-400">
              <span className="material-symbols-outlined text-[20px]">chevron_left</span>
            </button>
            <div className="text-center">
              <p className="text-[10px] font-bold text-primary uppercase tracking-widest">{viewDate.getFullYear()}</p>
              <p className="text-sm font-black text-white">{MONTHS[viewDate.getMonth()]}</p>
            </div>
            <button onClick={() => changeMonth(1)} className="p-1 hover:bg-slate-700/50 rounded-lg text-slate-400">
              <span className="material-symbols-outlined text-[20px]">chevron_right</span>
            </button>
          </div>

          {/* Weekdays */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {WEEKDAYS.map((d, i) => (
              <div key={i} className="text-[9px] font-black text-slate-700 uppercase text-center py-1">{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {renderDays()}
          </div>

          {/* Selected range display */}
          {value.start !== value.end && (
            <div className="mt-3 pt-3 border-t border-slate-800/50 flex items-center justify-between text-[10px]">
              <span className="text-slate-500">Período selecionado:</span>
              <span className="text-primary font-bold">{formatLabel(value)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
